// TCMB Efektif Satış kurları — kompakt widget (TopBar için)
import { useEffect, useState } from "react";
import { TrendingUp, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuLabel,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";

const CCYS = ["USD", "EUR", "GBP", "CHF"];
const fmt = (v) => Number(v || 0).toLocaleString("tr-TR", {
  minimumFractionDigits: 4, maximumFractionDigits: 4,
});

export function FxRatesWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/fx");
      setData(data);
    } catch { /* silent */ }
  };
  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/fx/refresh");
      setData(data);
      toast.success("Kurlar güncellendi");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 30 * 60 * 1000); // 30 dk
    return () => clearInterval(iv);
  }, []);

  const usd = data?.rates?.USD?.sell;
  const eur = data?.rates?.EUR?.sell;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="hidden md:flex items-center gap-2 h-9 px-2 text-xs font-medium tabular"
          data-testid="fx-widget-btn"
          title="TCMB Efektif Satış Kurları"
        >
          <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />
          {usd ? (
            <>
              <span data-testid="fx-usd">$ {fmt(usd)}</span>
              <span className="text-muted-foreground">·</span>
              <span data-testid="fx-eur">€ {fmt(eur)}</span>
            </>
          ) : (
            <span className="text-muted-foreground">TCMB</span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72" data-testid="fx-widget-menu">
        <DropdownMenuLabel className="flex items-center justify-between">
          <div>
            <div>TCMB Efektif Satış</div>
            <div className="text-xs text-muted-foreground font-normal">
              {data?.date || "—"}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={refresh}
            disabled={loading}
            data-testid="fx-refresh-btn"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <RefreshCw className="h-3.5 w-3.5"/>}
          </Button>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="p-2 space-y-1.5">
          {CCYS.map((c) => {
            const r = data?.rates?.[c];
            return (
              <div key={c} className="flex items-center justify-between px-2 py-1.5 rounded hover:bg-muted text-sm">
                <span className="font-medium w-12">{c}</span>
                <div className="flex-1 grid grid-cols-2 gap-3 text-right tabular text-xs">
                  <div>
                    <div className="text-muted-foreground">Alış</div>
                    <div>{r ? fmt(r.buy) : "—"}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Satış</div>
                    <div className="font-semibold text-emerald-600">{r ? fmt(r.sell) : "—"}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <DropdownMenuSeparator />
        <div className="px-3 py-2 text-[10px] text-muted-foreground">
          Kaynak: Türkiye Cumhuriyet Merkez Bankası · Her iş günü 15:30'da güncellenir
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
